// /src/engine/importLdraw.ts

import * as THREE from 'three';
import type { EditorSnapshot, PlacedPart, Submodel, SubmodelInstance } from '@/store/modelStore';
import { MAIN_SUBMODEL_ID } from '@/store/modelStore';
import { colorToHex } from './ldrawColors';
import { ldrawToThreeMatrix, parseLdrawLineType1, matrixToArray } from './ldrawMath';

function uid(prefix = 'p') {
  return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}

export function importLDraw(text: string): EditorSnapshot {
  // Upgraded importer:
  // - Supports MPD with subfile references as real instances (non-flattened).
  // - Preserves Brickerator metadata when present.
  // - Falls back to legacy single-file behavior.

  const normalized = text.replace(/\r\n/g, '\n');
  const mpdChunks = splitMpd(normalized);

  // Legacy: no MPD structure
  if (!mpdChunks.length) {
    return importSingleFile(normalized);
  }

  // Build chunk map (case-insensitive)
  const chunkMap = new Map<string, string>();
  for (const ch of mpdChunks) {
    chunkMap.set(ch.name.trim().toLowerCase(), ch.content);
  }

  const mainChunk = mpdChunks[0];
  const mainKey = mainChunk.name.trim().toLowerCase();

  // Submodel defs (prefer Brickerator metadata if present)
  const submodels: Submodel[] = [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }];
  const submodelById = new Map<string, Submodel>();
  submodelById.set(MAIN_SUBMODEL_ID, submodels[0]);

  for (const ch of mpdChunks) {
    for (const raw of ch.content.split('\n')) {
      const line = raw.trim();
      if (!line) continue;
      const mDef = line.match(/^0\s+!BRICKERATOR\s+SUBMODELDEF\s+(\S+)\s+(.+)$/i);
      if (mDef) {
        const id = mDef[1].trim();
        const name = mDef[2].trim();
        if (!submodelById.has(id)) {
          const sm = { id, name };
          submodels.push(sm);
          submodelById.set(id, sm);
        }
      }
    }
  }

  const stripExt = (n: string) => n.replace(/\.[^/.]+$/, '');
  const submodelIdForFile = (fileName: string): string => {
    const base = stripExt(fileName.trim());
    if (base.toLowerCase() === MAIN_SUBMODEL_ID) return MAIN_SUBMODEL_ID;
    if (submodelById.has(base)) return base;
    // Create a new submodel entry (generic MPD import)
    const sm = { id: base, name: base };
    submodels.push(sm);
    submodelById.set(base, sm);
    return base;
  };

  const parts: PlacedPart[] = [];
  const instances: SubmodelInstance[] = [];

  for (const ch of mpdChunks) {
    const fileName = ch.name.trim();
    const key = fileName.toLowerCase();
    const content = ch.content ?? '';

    const fileSubmodelId = key === mainKey ? MAIN_SUBMODEL_ID : submodelIdForFile(fileName);
    let step = 1;

    for (const raw of content.split('\n')) {
      const line = raw.trim();
      if (!line) continue;

      const mStep = line.match(/^0\s+!BRICKERATOR\s+STEP\s+(\d+)\b/i);
      if (mStep) {
        step = Math.max(1, Number(mStep[1]));
        continue;
      }
      if (/^0\s+STEP\b/i.test(line)) {
        step++;
        continue;
      }

      const parsed = parseLdrawLineType1(line);
      if (!parsed) continue;

      const localT = ldrawToThreeMatrix(parsed.matrixL);
      const refName = parsed.partFile.trim();
      const refKey = refName.toLowerCase();

      // Subfile reference -> real instance
      if (chunkMap.has(refKey)) {
        const childId = submodelIdForFile(refName);
        instances.push({
          id: uid('inst'),
          submodelId: childId,
          parentSubmodelId: fileSubmodelId,
          matrix: matrixToArray(localT),
          step,
        });
        continue;
      }

      // Normal part
      parts.push({
        id: uid('import'),
        partFile: refName,
        name: refName,
        ldrawColor: parsed.color,
        colorHex: colorToHex(parsed.color),
        matrix: matrixToArray(localT),
        step,
        submodelId: fileSubmodelId,
      });
    }
  }

  const currentStep = Math.max(
    1,
    ...parts.map((p) => p.step ?? 1),
    ...instances.map((i) => i.step ?? 1)
  );
  const stepStateBySubmodel = computeStepStateBySubmodel(parts, instances, submodels);
  const mainState = stepStateBySubmodel[MAIN_SUBMODEL_ID] ?? { currentStep, viewStep: 999 };
  return {
    version: 4,
    parts,
    instances,
    submodels,
    activeSubmodelId: null,
    selectedId: parts[0]?.id ?? instances[0]?.id ?? null,
    currentStep: mainState.currentStep,
    viewStep: mainState.viewStep,
    stepStateBySubmodel,
  };
}

export function importInternalJson(text: string): EditorSnapshot {
  const obj = JSON.parse(text);
  if (!obj || typeof obj !== 'object') throw new Error('Invalid JSON');
  
  const version = Number((obj as any).version ?? 1);

  // Back-compat: v1 only had parts[]
  if (version === 1) {
    if (!Array.isArray((obj as any).parts)) throw new Error('Unsupported file');
    const parts: PlacedPart[] = (obj as any).parts.map((p: any) => ({
      id: String(p.id ?? uid('p')),
      partFile: String(p.partFile ?? 'unknown.dat'),
      name: String(p.name ?? p.partFile ?? 'Part'),
      ldrawColor: Number(p.ldrawColor ?? 16),
      colorHex: String(p.colorHex ?? colorToHex(Number(p.ldrawColor ?? 16))),
      matrix: Array.isArray(p.matrix) ? p.matrix.map(Number) : new THREE.Matrix4().identity().elements.slice(),
      step: Number(p.step ?? 1),
      submodelId: MAIN_SUBMODEL_ID,
    }));
    const stepStateBySubmodel = computeStepStateBySubmodel(parts, [], [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }]);
    return {
      version: 4,
      parts,
      instances: [],
      submodels: [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }],
      activeSubmodelId: null,
      selectedId: parts[0]?.id ?? null,
      currentStep: stepStateBySubmodel[MAIN_SUBMODEL_ID]?.currentStep ?? Math.max(1, ...parts.map((p) => p.step)),
      viewStep: stepStateBySubmodel[MAIN_SUBMODEL_ID]?.viewStep ?? 999,
      stepStateBySubmodel,
   };
  }

  // Back-compat: v2 had parts + submodels, but no instances
  if (version === 2) {
    if (!Array.isArray((obj as any).parts)) throw new Error('Unsupported file');

    const rawSubmodels = Array.isArray((obj as any).submodels) ? (obj as any).submodels : [];
    const submodels: Submodel[] = rawSubmodels.map((sm: any) => ({
      id: String(sm.id ?? uid('sm')),
      name: String(sm.name ?? sm.id ?? 'Submodel'),
    }));
    if (!submodels.some((sm) => sm.id === MAIN_SUBMODEL_ID)) {
      submodels.unshift({ id: MAIN_SUBMODEL_ID, name: 'Main model' });
    }

    const parts: PlacedPart[] = (obj as any).parts.map((p: any) => ({
      id: String(p.id ?? uid('p')),
      partFile: String(p.partFile ?? 'unknown.dat'),
      name: String(p.name ?? p.partFile ?? 'Part'),
      ldrawColor: Number(p.ldrawColor ?? 16),
      colorHex: String(p.colorHex ?? colorToHex(Number(p.ldrawColor ?? 16))),
      matrix: Array.isArray(p.matrix) ? p.matrix.map(Number) : new THREE.Matrix4().identity().elements.slice(),
      step: Number(p.step ?? 1),
      submodelId: String(p.submodelId ?? MAIN_SUBMODEL_ID),
    }));

    const instances: SubmodelInstance[] = (Array.isArray((obj as any).instances) ? (obj as any).instances : []).map((i: any) => ({
    id: String(i.id ?? uid('inst')),
    submodelId: String(i.submodelId ?? MAIN_SUBMODEL_ID),
    parentSubmodelId: String(i.parentSubmodelId ?? MAIN_SUBMODEL_ID),
    matrix: Array.isArray(i.matrix) ? i.matrix.map(Number) : new THREE.Matrix4().identity().elements.slice(),
    step: Number(i.step ?? 1),
  }));

    const active =
      typeof (obj as any).activeSubmodelId === 'string' &&
      submodels.some((sm) => sm.id === (obj as any).activeSubmodelId)
        ? (obj as any).activeSubmodelId
        : null;

    const fileCurrentStep = Math.max(1, Number((obj as any).currentStep ?? Math.max(1, ...parts.map((p) => p.step))));
    const fileViewStep = Math.max(1, Number((obj as any).viewStep ?? 999));
    const stepStateBySubmodel = computeStepStateBySubmodel(parts, instances, submodels, {
      [MAIN_SUBMODEL_ID]: { currentStep: fileCurrentStep, viewStep: fileViewStep },
    });
    return {
      version: 4,
      parts,
      instances,
      submodels,
      activeSubmodelId: active,
      selectedId: (obj as any).selectedId ?? parts[0]?.id ?? null,
      currentStep: (stepStateBySubmodel[active ?? MAIN_SUBMODEL_ID]?.currentStep ?? fileCurrentStep),
      viewStep: (stepStateBySubmodel[active ?? MAIN_SUBMODEL_ID]?.viewStep ?? fileViewStep),
      stepStateBySubmodel,
    };
  }

  // v3 (legacy) and v4 (per-submodel step state)
  if (version !== 3 && version !== 4) throw new Error('Unsupported file');
  if (!Array.isArray((obj as any).parts)) throw new Error('Unsupported file');

  const rawSubmodels = Array.isArray((obj as any).submodels) ? (obj as any).submodels : [];
  const submodels: Submodel[] = rawSubmodels.map((sm: any) => ({
    id: String(sm.id ?? uid('sm')),
    name: String(sm.name ?? sm.id ?? 'Submodel'),
  }));
  if (!submodels.some((sm) => sm.id === MAIN_SUBMODEL_ID)) {
    submodels.unshift({ id: MAIN_SUBMODEL_ID, name: 'Main model' });
  }

  const parts: PlacedPart[] = (obj as any).parts.map((p: any) => ({
    id: String(p.id ?? uid('p')),
    partFile: String(p.partFile ?? 'unknown.dat'),
    name: String(p.name ?? p.partFile ?? 'Part'),
    ldrawColor: Number(p.ldrawColor ?? 16),
    colorHex: String(p.colorHex ?? colorToHex(Number(p.ldrawColor ?? 16))),
    matrix: Array.isArray(p.matrix) ? p.matrix.map(Number) : new THREE.Matrix4().identity().elements.slice(),
    step: Number(p.step ?? 1),
    submodelId: String(p.submodelId ?? MAIN_SUBMODEL_ID),
  }));

  const instances: SubmodelInstance[] =
  (Array.isArray((obj as any).instances) ? (obj as any).instances : []).map((i: any) => ({
    id: String(i.id ?? uid('inst')),
    submodelId: String(i.submodelId ?? MAIN_SUBMODEL_ID),
    parentSubmodelId: String(i.parentSubmodelId ?? MAIN_SUBMODEL_ID),
    matrix: Array.isArray(i.matrix) ? i.matrix.map(Number) : new THREE.Matrix4().identity().elements.slice(),
    step: Number(i.step ?? 1),
  }));

  const active =
    typeof (obj as any).activeSubmodelId === 'string' &&
    submodels.some((sm) => sm.id === (obj as any).activeSubmodelId)
      ? (obj as any).activeSubmodelId
      : null;

  const fileCurrentStep = Math.max(1, Number((obj as any).currentStep ?? Math.max(1, ...parts.map((p) => p.step ?? 1))));
  const fileViewStep = Math.max(1, Number((obj as any).viewStep ?? 999));
  const rawStepMap = version === 4 ? ((obj as any).stepStateBySubmodel ?? null) : null;
  const stepStateBySubmodel = computeStepStateBySubmodel(parts, instances, submodels, rawStepMap ?? {
    [MAIN_SUBMODEL_ID]: { currentStep: fileCurrentStep, viewStep: fileViewStep },
  });
  const ctx = active ?? MAIN_SUBMODEL_ID;
  const ctxState = stepStateBySubmodel[ctx] ?? stepStateBySubmodel[MAIN_SUBMODEL_ID] ?? { currentStep: fileCurrentStep, viewStep: fileViewStep };
  return {
    version: 4,
    parts,
    instances,
    submodels,
    activeSubmodelId: active,
    selectedId: (obj as any).selectedId ?? parts[0]?.id ?? null,
    currentStep: ctxState.currentStep,
    viewStep: ctxState.viewStep,
    stepStateBySubmodel,
  };
}

type MpdChunk = { name: string; content: string };
function splitMpd(text: string): MpdChunk[] {
  const lines = text.split('\n');
  const chunks: MpdChunk[] = [];
  let cur: MpdChunk | null = null;
  for (const ln of lines) {
    const m = ln.match(/^0\s+FILE\s+(.+)$/i);
    if (m) {
      if (cur) chunks.push(cur);
      cur = { name: m[1].trim(), content: '' };
      continue;
    }
    if (/^0\s+NOFILE\b/i.test(ln)) {
      if (cur) chunks.push(cur);
      cur = null;
      continue;
    }
    if (cur) cur.content += ln + '\n';
  }
  if (cur) chunks.push(cur);
  return chunks;
}

function importSingleFile(content: string): EditorSnapshot {
  const submodels: Submodel[] = [{ id: MAIN_SUBMODEL_ID, name: 'Main model' }];
  const submodelById = new Map<string, Submodel>();
  submodelById.set(MAIN_SUBMODEL_ID, submodels[0]);

  let curSubmodelId = MAIN_SUBMODEL_ID;
  let step = 1;
  const parts: PlacedPart[] = [];

  const lines = content.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    // Brickerator explicit step marker (preferred when present)
    const mStep = line.match(/^0\s+!BRICKERATOR\s+STEP\s+(\d+)\b/i);
    if (mStep) {
      step = Math.max(1, Number(mStep[1]));
      continue;
    }

    // Standard step marker
    if (/^0\s+STEP\b/i.test(line)) {
      step++;
      continue;
    }

    // Brickerator metadata
    const mDef = line.match(/^0\s+!BRICKERATOR\s+SUBMODELDEF\s+(\S+)\s+(.+)$/i);
    if (mDef) {
      const id = mDef[1].trim();
      const name = mDef[2].trim();
      if (!submodelById.has(id)) {
        const sm = { id, name };
        submodels.push(sm);
        submodelById.set(id, sm);
      }
      continue;
    }
    const mBegin = line.match(/^0\s+!BRICKERATOR\s+SUBMODELBEGIN\s+(\S+)(?:\s+(.+))?$/i);
    if (mBegin) {
      const id = mBegin[1].trim();
      const name = (mBegin[2] ?? id).trim();
      if (!submodelById.has(id)) {
        const sm = { id, name };
        submodels.push(sm);
        submodelById.set(id, sm);
      }
      curSubmodelId = id;
      continue;
    }
    const mEnd = line.match(/^0\s+!BRICKERATOR\s+SUBMODELEND\b/i);
    if (mEnd) {
      curSubmodelId = MAIN_SUBMODEL_ID;
      continue;
    }

    const parsed = parseLdrawLineType1(line);
    if (!parsed) continue;
    const mT = ldrawToThreeMatrix(parsed.matrixL);
    parts.push({
      id: uid('import'),
      partFile: parsed.partFile.trim(),
      name: parsed.partFile.trim(),
      ldrawColor: parsed.color,
      colorHex: colorToHex(parsed.color),
      matrix: matrixToArray(mT),
      step,
      submodelId: curSubmodelId,
    });
  }

  const currentStep = Math.max(1, step, ...parts.map((p) => p.step ?? 1));
  const stepStateBySubmodel = computeStepStateBySubmodel(parts, [], submodels, {
    [MAIN_SUBMODEL_ID]: { currentStep, viewStep: 999 },
  });
  return {
    version: 4,
    parts,
    instances: [],
    submodels,
    activeSubmodelId: null,
    selectedId: parts[0]?.id ?? null,
    currentStep: stepStateBySubmodel[MAIN_SUBMODEL_ID]?.currentStep ?? currentStep,
    viewStep: stepStateBySubmodel[MAIN_SUBMODEL_ID]?.viewStep ?? 999,
    stepStateBySubmodel,
  };
}

type StepState = { currentStep: number; viewStep: number };
function computeStepStateBySubmodel(
  parts: PlacedPart[],
  instances: SubmodelInstance[],
  submodels: Submodel[],
  override?: Record<string, StepState> | null
): Record<string, StepState> {
  const out: Record<string, StepState> = Object.create(null);
  for (const sm of submodels) out[sm.id] = { currentStep: 1, viewStep: 999 };
  if (!out[MAIN_SUBMODEL_ID]) out[MAIN_SUBMODEL_ID] = { currentStep: 1, viewStep: 999 };

  const maxBy = new Map<string, number>();
  for (const p of parts) {
    const sid = (p.submodelId ?? MAIN_SUBMODEL_ID);
    maxBy.set(sid, Math.max(maxBy.get(sid) ?? 1, Math.max(1, p.step ?? 1)));
  }
  for (const i of instances) {
    const pid = (i.parentSubmodelId ?? MAIN_SUBMODEL_ID);
    maxBy.set(pid, Math.max(maxBy.get(pid) ?? 1, Math.max(1, i.step ?? 1)));
  }
  for (const [id, mx] of maxBy.entries()) {
    if (!out[id]) out[id] = { currentStep: mx, viewStep: 999 };
    else out[id].currentStep = Math.max(out[id].currentStep, mx);
  }

  if (override && typeof override === 'object') {
    for (const [k, v] of Object.entries(override)) {
      const cur = Math.max(1, Math.floor(Number((v as any).currentStep ?? 1)));
      const view = Math.max(1, Math.floor(Number((v as any).viewStep ?? 999)));
      out[k] = { currentStep: cur, viewStep: view };
    }
  }
  return out;
}
